import {camelToTitle, getBaseUrl, mergeProperties, reverseEnum} from "../components/Utility";
import moment from "moment-timezone";
import CopyButton from "../components/common/button/CopyButton";
import {PaymentStatus} from "../enums/enums";
import React from "react";

const timezone = 'Asia/Kolkata';
const baseUrl = getBaseUrl();

export const getFormData = (data) => {
    const initialData = {
        name: '',
        transactionType: '',
        dueDays: '',
        contactInfo: {
            phone: '',
            address: {
                street: '',
                city: '',
                state: '',
                zipCode: ''
            }
        },
    }
    // fill data in initialData from data
    return mergeProperties(initialData, data);
};

export const getTableColumns = ({ActionDropdown, deleteHandler, resetHandler}) => {
    let tableColumns = [
        {header: "Name", field: "name"},
        {header: "Phone", field: "phone"},
        {header: "Due date", field: "dueDate"},
        {header: "Due Amount", field: "dueAmount"},
        {header: "Advanced amount", field: "advancedAmount"},
        {header: "Upload Document", field: "documentUpload"},
    ];
    tableColumns.push({
        header: 'Action',
        disableSorting: true,
        field: 'action',
        align: "right",
        render: (row) => {
            return <ActionDropdown row={row} purchase={true} view={false} onDelete={deleteHandler}
                                   onReset={resetHandler}
                                   restricted={[{BranchManager: ['b', 'c', 'h', 'm'],}]}/>;
        },
    });
    return tableColumns;
};

export const getTableData = ({items = [], categories}) => {
    if (!Array.isArray(items)) return [];

    return items.map(item => {
        return {
            id: item.id,
            name: item.name,
            phone: item?.contactInfo?.phone,
            dueDate: item.dueDate
                ? moment(item.dueDate).tz(timezone).format("MMM DD, YYYY, hh:mm A")
                : "-",
            advancedAmount: item?.advancedAmount,
            dueAmount: item?.dueAmount,
            documentUpload: item?.dashboardUrl
                ? <CopyButton url={`${baseUrl}${item.dashboardUrl}`}/>
                : null
        };
    });
};


export const getDetailData = ({item, list}) => {
    if (!item) return {details: {}};
    const {
        items: expenseList,
        status: expenseListStatus,
    } = list.expense;

    return {
        details: {
            id: item.id,
            name: item.name,
            phone: item?.contactInfo.phone,
            dueDate: item.dueDate ? moment(item.dueDate).tz(timezone).format('MMM DD, YYYY, hh:mm A') : "-",
            advancedAmount: item?.advancedAmount,
            vendorType: expenseListStatus === 'success' ? item.transactionType?.map(id => expenseList[id]).join(', ') : '-',
            dueDays: item.dueDays,
            documentUpload: item?.dashboardUrl
                ? <CopyButton url={`${baseUrl}${item.dashboardUrl}`}/>
                : null
        }
    };
}

export const getVendorHistoryTableColumns = () => {
    let tableColumns = [
        {header: "Billing Amount", field: "totalAmount"},
        {header: "Payment Made", field: "paidAmount"},
        {header: "Type", field: "type"},
        {header: "Payment Difference", field: "difference"},
        {header: "Total Due", field: "totalDue"},
        {header: "Total Advanced", field: "totalAdvanced"},
        {header: "Payment Method", field: "paymentMethod"},
        {header: "Pay At", field: "payAt"},
    ];
    return tableColumns;
};

export const getVendorHistoryTableData = ({items = []}) => {
    if (!Array.isArray(items)) return [];

    return items.map(item => {
        let difference = 'N/A';
        let totalAmount = 'N/A';

        if (item.type === 'Initial Payment Update') {
            if (
                typeof item.oldPurchaseTotalAmount === 'number' &&
                typeof item.purchaseOrder?.totalAmount === 'number' &&
                typeof item.payAmount === 'number'
            ) {
                const diffValue =
                    (item.oldPurchaseTotalAmount - item.newPurchaseOrderTotalAmount) +
                    item.payAmount;

                const absValue = Math.abs(diffValue);
                const sign = absValue === 0 ? '' : diffValue >= 0 ? '+' : '-';

                // Only add border/classes if value is not zero
                const cls =
                    absValue > 0
                        ? diffValue >= 0
                            ? 'border border-success px-1 rounded-pill text-success'
                            : 'border border-danger px-1 rounded-pill text-danger'
                        : '';

                difference = (
                    <span className={cls}>
                        {sign} {absValue}
                    </span>
                );
            }

            totalAmount =
                item.purchaseOrder?.totalAmount === item.oldPurchaseTotalAmount
                    ? item.purchaseOrder?.totalAmount ?? 'N/A'
                    : (
                        <>
                            {item.purchaseOrder?.totalAmount ?? 'N/A'}{" "}
                            <span
                                className="border border-warning text-warning px-1 rounded-pill"
                            >
                                {item.oldPurchaseTotalAmount ?? ""}
                            </span>
                        </>
                    );
        } else {
            if (
                typeof item.purchaseOrder?.totalAmount === 'number' &&
                typeof item.purchaseOrder?.paidAmount === 'number'
            ) {
                const rawDiff = item.purchaseOrder.totalAmount - item.purchaseOrder.paidAmount;

                const isPositive = rawDiff < 0; // More paid than total
                const absValue = Math.abs(rawDiff);
                const sign = absValue === 0 ? '' : isPositive ? '+' : '-';

                // Only add border/classes if value is not zero
                const cls =
                    absValue > 0
                        ? isPositive
                            ? 'border border-success px-1 rounded-pill text-success'
                            : 'border border-danger px-1 rounded-pill text-danger'
                        : '';

                difference = (
                    <span className={cls}>
                              {sign} {absValue}
                            </span>
                );
            }

            totalAmount = item.purchaseOrder?.totalAmount ?? 'N/A';
        }

        return {
            totalAmount,
            paidAmount: (
                <span
                    className={`fs-md px-1 rounded-pill ${((item.purchaseOrder?.paidAmount ?? item.payAmount) > 0) && 'border text-primary border-primary'}`}>
                      {item.purchaseOrder?.paidAmount ?? item.payAmount ?? 'N/A'}
                </span>
            ),
            difference,
            type: (
                <span className={item.purchaseOrder?.status === -1 ? 'his_del' : ''}>
                    {item.type}
                 </span>
            ),
            totalDue: (
                <span
                    className={`fs-md px-1 rounded-pill ${item.totalDue ? 'bg-danger bg-opacity-75 text-white' : ''}`}>
                             {item.totalDue ?? 'N/A'}
                </span>
            ),
            totalAdvanced: (<span
                className={`fs-md px-1 rounded-pill ${item.vendorAdvanceAmount ? 'bg-info bg-opacity-75 text-white' : ''}`}>{item.vendorAdvanceAmount ?? 'N/A'}</span>),
            paymentMethod: item.paymentMethod,
            payAt: moment(item.payAt).tz(timezone).format('MMM DD, YYYY, hh:mm A')
        };
    });
};


export const getVendorDocTableColumns = () => {
    let tableColumns = [
        {header: "S No.", field: "sNo"},
        {header: "Total Amount", field: "totalAmount"},
        {header: "Receipt", field: "receipt"},
        {header: "Created At", field: "createdAt"},
        {header: "Status", field: "status"}
    ];
    return tableColumns;
};

export const getVendorDocTableData = ({items = []}) => {
    if (!Array.isArray(items)) return [];

    const statusClasses = {
        paid: "bg-success text-white rounded-pill px-4",
        success: "bg-success text-white rounded-pill px-4",
        pending: "bg-warning text-dark rounded-pill px-4",
        failed: "bg-danger text-white rounded-pill px-4",
    };

    return items.map((item, index) => {
        const statusText = reverseEnum(PaymentStatus)[item.paymentStatus].toLowerCase();
        const isPdf = item?.receipt?.toLowerCase().endsWith(".pdf");
        return {
            sNo: index + 1,
            totalAmount: item.totalAmount,
            receipt: item.receipt
                ? isPdf ? (
                    <button
                        type="button"
                        className="btn btn-sm text-primary px-0"
                        onClick={() => window.open(`${process.env.REACT_APP_API_BASE_URL}/${item.receipt}`, "_blank")}
                    >
                        View Receipt
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-sm text-primary px-0"
                        onClick={() => window.open(`${process.env.REACT_APP_API_BASE_URL}/${item.receipt}`, "_blank")}
                    >
                        View Receipt
                    </button>
                )
                : "-",
            status: <span
                className={`px-2 py-1 rounded ${statusClasses[statusText] || "bg-secondary text-white"}`}>{camelToTitle(statusText)}</span>,
            purchaseDate: moment(item.purchaseDate).tz(timezone).format("MMM DD, YYYY, hh:mm A"),
            createdAt: moment(item.createdAt).tz(timezone).format("MMM DD, YYYY, hh:mm A"),
        };
    });
};